/**
 * The type system: what a scope holds, and what an expression is worth.
 *
 * Two kinds of type, and the interesting part is how they relate.
 *
 * **Scalars** — `int`, `float`, `string` — are what a column holds when it holds
 * a plain value: `g.cost` is a float, `g.fuel` a string, a timestep's `t` an int.
 *
 * **Relation types** are rows: `b` in `b: battery` is a `battery`, and so is
 * anything that references one. A relation type is *nominal over a scalar* —
 * `line.from` is a `bus`, and a bus is a string underneath; `t` is a `timestep`,
 * and a timestep is an int. That's what makes `t + 1` mean something and
 * `l.from + 1` mean nothing: arithmetic reaches through the name to the scalar,
 * and only some scalars add. It's also what makes `l.from == n` a sensible
 * comparison and `l.from == g` a suspicious one, even though both are "a string
 * compared with a string" once you look through.
 *
 * So the rule is: a relation type behaves as its key's type wherever a value is
 * wanted, and keeps its name wherever an identity is wanted. Attributes are
 * reached through the name — `columnTypeOf` — and a relation with a composite
 * key has no scalar underneath at all, because a `demand` row is a (bus,
 * timestep) pair rather than a number.
 *
 * ## Scope
 *
 * A scope maps a bound variable to its type, and the binders are the same three
 * the rest of the language has: a rule's parameters, a comprehension's clauses,
 * and — for a filter — the quantifier it belongs to. {@link scopeOfRule} builds
 * the outer one; {@link boundIn} extends it for a sum. Nothing else binds, so
 * there's no environment to thread beyond those.
 */

import {
  Catalog,
  RelationSchema,
  ScalarKind,
  columnsOf,
  foreignKeys,
  isValueCoercible,
} from './schema';
import { Expr, Quantifier, Rule } from './ruleAst';

export type Type =
  | { kind: 'scalar'; name: ScalarKind }
  /** A row of, or a reference to, a relation — `battery`, `bus`, `timestep`. */
  | { kind: 'relation'; name: string }
  /** Not worked out: an unbound variable, an unknown relation, a bad column. */
  | { kind: 'unknown' };

export const INT: Type = { kind: 'scalar', name: 'int' };
export const FLOAT: Type = { kind: 'scalar', name: 'float' };
export const STRING: Type = { kind: 'scalar', name: 'string' };
export const UNKNOWN: Type = { kind: 'unknown' };

export const relationType = (name: string): Type => ({ kind: 'relation', name });

export const typeToString = (type: Type): string =>
  type.kind === 'unknown' ? '?' : type.name;

/** Whether two types are the same — a relation is only ever itself. */
export const sameType = (a: Type, b: Type): boolean =>
  a.kind === b.kind && (a.kind === 'unknown' || a.name === (b as { name: string }).name);

// ---------------------------------------------------------------------------
// Relations
// ---------------------------------------------------------------------------

/**
 * What a column holds. A column that references another relation *is* that
 * relation (`line.from` is a `bus`); everything else is the scalar the schema
 * declares, defaulting to float — except where the schema says `unknown`, which
 * is a column whose type is still to be worked out (see {@link typedCatalog})
 * and not a claim that it holds a number.
 */
export function columnTypeOf(schema: RelationSchema, column: string, catalog: Catalog): Type {
  if (!columnsOf(schema).includes(column)) return UNKNOWN;
  const referenced = foreignKeys(schema, catalog)[column];
  if (referenced) return relationType(referenced);
  const declared = schema.types?.[column];
  return declared === 'unknown' ? UNKNOWN : { kind: 'scalar', name: declared ?? 'float' };
}

/**
 * What an applied reference to this relation is worth — the type of its single
 * non-key column. Unknown for an entity table, which has several and so isn't
 * value-coercible in the first place.
 */
export function valueTypeOf(schema: RelationSchema, catalog: Catalog): Type {
  if (!isValueCoercible(schema)) return UNKNOWN;
  return columnTypeOf(schema, schema.values[0], catalog);
}

/**
 * The scalar a type is underneath: a relation resolves to its key's type, and a
 * key that is itself a reference keeps resolving. Composite keys have no scalar
 * — a row of `demand` is a pair, not a number — and neither does anything
 * unknown.
 *
 * `seen` breaks a cycle of references, which the schema doesn't forbid.
 */
export function underlyingScalar(
  type: Type,
  catalog: Catalog,
  seen: ReadonlySet<string> = new Set(),
): Type {
  if (type.kind !== 'relation') return type;
  if (seen.has(type.name)) return UNKNOWN;
  const schema = catalog[type.name];
  if (!schema || schema.keys.length !== 1) return UNKNOWN;
  return underlyingScalar(
    columnTypeOf(schema, schema.keys[0], catalog),
    catalog,
    new Set([...seen, type.name]),
  );
}

/** `int` or `float` if the type counts as a number once resolved, else undefined. */
export function numericKind(type: Type, catalog: Catalog): 'int' | 'float' | undefined {
  const scalar = underlyingScalar(type, catalog);
  if (scalar.kind !== 'scalar') return undefined;
  return scalar.name === 'string' ? undefined : scalar.name;
}

/**
 * A relation's signature, the way its panel and its hover show it:
 *
 *   solar_strength(timestep: timestep) -> (value: float)
 *   gen(id: string) -> (min: float, max: float, cost: float, fuel: string)
 *   SOC(battery: battery, soc_time: soc_time) -> (value: float)
 *
 * One grammar for every relation: `name: type` for every column — keys and
 * values alike — and the values as a parenthesised tuple after `->`. A relation
 * is value-coercible exactly when that tuple has a single entry, so an applied
 * reference (`SOC(b, t)`) reads off that one value; you can see it straight from
 * the signature by counting the tuple. A pure index set with no value columns
 * (`timestep`) is just its head, with no `->`.
 */
export function signatureOf(schema: RelationSchema, catalog: Catalog): string {
  const column = (name: string): string => `${name}: ${typeToString(columnTypeOf(schema, name, catalog))}`;
  const head = `${schema.name}(${schema.keys.map(column).join(', ')})`;
  if (!schema.values.length) return head;
  return `${head} -> (${schema.values.map(column).join(', ')})`;
}

// ---------------------------------------------------------------------------
// Scope
// ---------------------------------------------------------------------------

/** The variables in scope at a point, and what each one is. */
export type TypeScope = ReadonlyMap<string, Type>;

/** `scope` plus what these quantifiers bind — a rule's parameters, a sum's clauses. */
export function boundIn(quantifiers: Quantifier[], scope: TypeScope, catalog: Catalog): TypeScope {
  const child = new Map(scope);
  for (const q of quantifiers) {
    child.set(q.var, catalog[q.relation] ? relationType(q.relation) : UNKNOWN);
  }
  return child;
}

/** The scope a rule's body is checked in: its parameters. */
export function scopeOfRule(rule: Rule, catalog: Catalog): TypeScope {
  return boundIn(rule.quantifiers, new Map(), catalog);
}

// ---------------------------------------------------------------------------
// Inference
// ---------------------------------------------------------------------------

/** The wider of two numeric kinds: ints stay ints only while everything is. */
const widen = (left: 'int' | 'float', right: 'int' | 'float'): Type =>
  left === 'int' && right === 'int' ? INT : FLOAT;

/**
 * What an expression is worth, in a scope.
 *
 * Arithmetic resolves its operands through their relation names to the scalars
 * underneath, so `t + 1` is an int where `t: timestep` — the result is the
 * scalar, not the timestep, since one past an hour index isn't itself an hour
 * of anything. Division always lands on float. Anything the schema can't answer
 * for is `unknown`, which spreads without complaining: this infers types, it
 * doesn't reject programs.
 */
export function typeOfExpr(e: Expr, scope: TypeScope, catalog: Catalog): Type {
  switch (e.kind) {
    case 'num':
      return Number.isInteger(e.value) ? INT : FLOAT;
    case 'str':
      return STRING;
    case 'ref':
      return scope.get(e.name) ?? UNKNOWN;
    case 'attr': {
      const owner = scope.get(e.obj);
      if (!owner || owner.kind !== 'relation') return UNKNOWN;
      const schema = catalog[owner.name];
      return schema ? columnTypeOf(schema, e.col, catalog) : UNKNOWN;
    }
    case 'apply': {
      const schema = catalog[e.relation];
      return schema ? valueTypeOf(schema, catalog) : UNKNOWN;
    }
    case 'call': {
      // `sqrt`/`sin`/`cos` always land on a float; `min`/`max` are as wide as
      // their widest argument, so `min(g.max, 1)` is a float and `min(t, 3)` an int.
      if (e.fn === 'sqrt' || e.fn === 'sin' || e.fn === 'cos') return FLOAT;
      const kinds = e.args.map((a) => numericKind(typeOfExpr(a, scope, catalog), catalog));
      if (kinds.length === 0 || kinds.some((kind) => kind === undefined)) return UNKNOWN;
      return kinds.includes('float') ? FLOAT : INT;
    }
    case 'neg':
      return numericKind(typeOfExpr(e.operand, scope, catalog), catalog) === 'int' ? INT : FLOAT;
    case 'binary': {
      const left = numericKind(typeOfExpr(e.left, scope, catalog), catalog);
      const right = numericKind(typeOfExpr(e.right, scope, catalog), catalog);
      if (!left || !right) return UNKNOWN;
      // A quotient is a fraction even when both sides are whole.
      return e.op === '/' ? FLOAT : widen(left, right);
    }
    case 'sum':
      // A sum is worth what it sums, over the scope its clauses open.
      return typeOfExpr(e.body, boundIn(e.over, scope, catalog), catalog);
    case 'cond': {
      // Both branches are values of the one conditional, so its type is
      // whatever they agree on — exactly, or as numbers.
      const then = typeOfExpr(e.then, scope, catalog);
      const otherwise = typeOfExpr(e.otherwise, scope, catalog);
      if (sameType(then, otherwise)) return then;
      const left = numericKind(then, catalog);
      const right = numericKind(otherwise, catalog);
      return left && right ? widen(left, right) : UNKNOWN;
    }
  }
}

// ---------------------------------------------------------------------------
// Typing the relations the rules themselves declare
// ---------------------------------------------------------------------------

/**
 * Give each derived relation the type of the rule that computes it.
 *
 * A derived relation's value is whatever its body works out to, so `gen_cap` is
 * a float because `min(g.max, wind_strength(t))` is, and a definition returning
 * a row — `def home(g: gen): g.bus` — makes its value a reference to that
 * relation rather than a number. The catalog can't know this when it's built,
 * because the answer is the rule's body and the body is checked against the
 * catalog; so it's a second pass over the rules.
 *
 * One pass isn't always enough, because a pass reads the rules in the order
 * they're listed and a definition may read one listed after it:
 *
 *   def label(g: gen): tag(g)      -- reads `tag`, which is still float here
 *   def tag(g: gen): g.fuel        -- and only becomes a string on this line
 *
 * The first pass types `label` against a `tag` that hasn't been worked out yet
 * and lands on the default; the second, with `tag` known to be a string, gets
 * it right. Each pass resolves at least the deepest definition still waiting,
 * so `n` rules take at most `n` passes — a chain of four listed in reverse
 * order takes exactly four — and that's the bound. Rules listed in dependency
 * order, which is the normal case, settle in one.
 *
 * A cycle (`a` reads `b` reads `a`) never resolves and never spins: neither
 * type changes, so the first pass is also the last, and both stay `unknown`.
 * That's why a derived value starts there rather than at float — anything still
 * unknown when this finishes is exactly the set of definitions that couldn't be
 * worked out, and it shows as `?` instead of passing for a number.
 *
 * Decision relations need no such pass: the solver assigns real numbers.
 */
export function typedCatalog(catalog: Catalog, derivedRules: Rule[]): Catalog {
  if (derivedRules.length === 0) return catalog;
  let current: Catalog = catalog;

  for (let pass = 0; pass < derivedRules.length; pass++) {
    let changed = false;
    const next: Catalog = { ...current };

    for (const rule of derivedRules) {
      const schema = next[rule.name];
      if (!schema || rule.body.kind !== 'define') continue;
      const type = typeOfExpr(rule.body.expr, scopeOfRule(rule, next), next);
      const value = schema.values[0];
      if (!value || type.kind === 'unknown') continue;
      if (sameType(columnTypeOf(schema, value, next), type)) continue;
      next[rule.name] =
        type.kind === 'relation'
          ? { ...schema, refs: { ...schema.refs, [value]: type.name } }
          : { ...schema, types: { ...schema.types, [value]: type.name } };
      changed = true;
    }

    current = next;
    if (!changed) break;
  }
  return current;
}
